const STORAGE_KEY = 'verella:wishlist:v1';
const STORAGE_VERSION = 1;
const MAX_ITEMS = 100;
const DRAWER_CLOSE_DURATION = 240;
const SHARE_PARAMETER = 'wishlist';
const SHARE_VERSION = 'v3';
const PREVIOUS_SHARE_VERSION = 'v2';
const LEGACY_SHARE_VERSION = 'v1';
const SHARE_MAX_ITEMS = MAX_ITEMS;
const SHARED_LINK_MAX_LENGTH = 8000;
const PRODUCT_FETCH_CONCURRENCY = 4;

if (!window.VerellaWishlist) {
  let items = [];
  let sharedItems = null;
  let closeTimer;
  let lastFocusedElement;
  let renderFrame;
  let statusTimer;
  let cartActionToken = 0;
  let cartActionInFlight = false;
  let shareActionInFlight = false;

  const productRequests = new Map();

  const html = document.documentElement;
  let translationCache;

  function translations() {
    if (translationCache) return translationCache;

    const source = document.querySelector('[data-verella-wishlist-translations]');
    try {
      translationCache = JSON.parse(source?.textContent || '{}');
    } catch (_error) {
      translationCache = {};
    }
    return translationCache;
  }

  function translate(key, replacements = {}) {
    let value = cleanText(translations()[key], 1000) || key;
    for (const [name, replacement] of Object.entries(replacements)) {
      value = value.replaceAll(`[${name}]`, String(replacement));
    }
    return value;
  }

  function translateCount(oneKey, otherKey, count, replacements = {}) {
    return translate(count === 1 ? oneKey : otherKey, { count, ...replacements });
  }

  function drawerItems() {
    return Array.isArray(sharedItems) ? sharedItems : items;
  }

  function cleanText(value, maximumLength = 300) {
    if (typeof value !== 'string' && typeof value !== 'number') return '';
    return String(value).trim().slice(0, maximumLength);
  }

  function safeProductUrl(value) {
    const candidate = cleanText(value, 1000);
    if (!candidate) return '/collections/all';

    try {
      const url = new URL(candidate, window.location.origin);
      if (url.origin !== window.location.origin) return '/collections/all';
      return `${url.pathname}${url.search}${url.hash}`;
    } catch (_error) {
      return '/collections/all';
    }
  }

  function safeImageUrl(value) {
    const candidate = cleanText(value, 1500);
    if (!candidate) return '';

    try {
      const url = new URL(candidate, window.location.origin);
      if (url.protocol !== 'https:' && url.protocol !== 'http:') return '';
      return url.href;
    } catch (_error) {
      return '';
    }
  }

  function safeHandle(value) {
    const handle = cleanText(value, 255).toLowerCase();
    return /^[a-z0-9][a-z0-9_-]*$/.test(handle) ? handle : '';
  }

  function safeVariantId(value) {
    const variantId = cleanText(value, 80);
    return /^\d+$/.test(variantId) ? variantId : '';
  }

  function productReferenceFromUrl(value) {
    const candidate = cleanText(value, 1000);
    if (!candidate) return { handle: '', variantId: '' };

    try {
      const url = new URL(candidate, window.location.origin);
      if (url.origin !== window.location.origin) return { handle: '', variantId: '' };

      const pathSegments = url.pathname.split('/').filter(Boolean);
      const productsIndex = pathSegments.lastIndexOf('products');
      let handle = '';

      if (productsIndex >= 0 && pathSegments[productsIndex + 1]) {
        try {
          handle = safeHandle(decodeURIComponent(pathSegments[productsIndex + 1]));
        } catch (_error) {
          handle = '';
        }
      }

      return {
        handle,
        variantId: safeVariantId(url.searchParams.get('variant')),
      };
    } catch (_error) {
      return { handle: '', variantId: '' };
    }
  }

  function variantIdToBase36(value) {
    const variantId = safeVariantId(value);
    if (!variantId) return '';

    try {
      return BigInt(variantId).toString(36);
    } catch (_error) {
      return '';
    }
  }

  function base36ToVariantId(value) {
    const encoded = cleanText(value, 80).toLowerCase();
    if (!/^[0-9a-z]+$/.test(encoded)) return '';

    try {
      let variantId = 0n;
      for (const character of encoded) {
        const digit = Number.parseInt(character, 36);
        if (!Number.isInteger(digit) || digit < 0 || digit >= 36) return '';
        variantId = variantId * 36n + BigInt(digit);
      }
      return safeVariantId(variantId.toString());
    } catch (_error) {
      return '';
    }
  }

  function storefrontRoot() {
    const dialogRoot = document.querySelector('[data-verella-wishlist-dialog]')?.dataset.rootUrl;
    const configuredRoot = cleanText(dialogRoot || window.Shopify?.routes?.root || '/', 200);
    const root = configuredRoot.startsWith('/') ? configuredRoot : '/';
    return root.endsWith('/') ? root : `${root}/`;
  }

  function previewThemeId() {
    try {
      const currentThemeId = safeVariantId(
        new URL(window.location.href).searchParams.get('preview_theme_id')
      );
      if (currentThemeId) return currentThemeId;
    } catch (_error) {
      // Fall back to the current theme metadata rendered by Liquid.
    }

    const dialog = document.querySelector('[data-verella-wishlist-dialog]');
    const themeId = safeVariantId(dialog?.dataset.themeId || window.Shopify?.theme?.id);
    const themeRole = cleanText(
      dialog?.dataset.themeRole || window.Shopify?.theme?.role,
      40
    ).toLowerCase();

    return themeId && themeRole && themeRole !== 'main' ? themeId : '';
  }

  function formatPrice(value) {
    const cents = Number(value);
    if (!Number.isFinite(cents)) return '';

    const locale = document.documentElement.lang || 'de-DE';
    const currency = cleanText(window.Shopify?.currency?.active || 'EUR', 3) || 'EUR';

    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
      }).format(cents / 100);
    } catch (_error) {
      return `${(cents / 100).toFixed(2)} ${currency}`;
    }
  }

  async function mapWithConcurrency(values, concurrency, worker) {
    const results = new Array(values.length);
    let nextIndex = 0;

    async function run() {
      while (nextIndex < values.length) {
        const index = nextIndex;
        nextIndex += 1;

        try {
          results[index] = await worker(values[index], index);
        } catch (_error) {
          results[index] = null;
        }
      }
    }

    const workerCount = Math.min(Math.max(1, concurrency), values.length);
    await Promise.all(Array.from({ length: workerCount }, run));
    return results;
  }

  function normalizeItem(value) {
    if (!value || typeof value !== 'object') return null;

    const id = cleanText(value.id, 80);
    if (!id) return null;

    const timestamp = Number(value.addedAt);
    const urlReference = productReferenceFromUrl(value.url);

    return {
      id,
      handle: safeHandle(value.handle) || urlReference.handle,
      // The product URL is updated at the exact moment a customer chooses a
      // swatch. Prefer its variant query over an older stored ID so wishlists
      // created with a previous script version keep the intended colour.
      variantId: urlReference.variantId || safeVariantId(value.variantId),
      variantTitle: cleanText(value.variantTitle, 300),
      url: safeProductUrl(value.url),
      title: cleanText(value.title, 300) || translate('product_default'),
      image: safeImageUrl(value.image),
      price: cleanText(value.price, 100),
      addedAt: Number.isFinite(timestamp) ? timestamp : Date.now(),
    };
  }

  function normalizeItems(value) {
    if (!Array.isArray(value)) return [];

    const seen = new Set();
    const normalized = [];

    for (const candidate of value) {
      const item = normalizeItem(candidate);
      if (!item || seen.has(item.id)) continue;

      seen.add(item.id);
      normalized.push(item);

      if (normalized.length >= MAX_ITEMS) break;
    }

    return normalized;
  }

  function readItems() {
    try {
      const storedValue = window.localStorage.getItem(STORAGE_KEY);
      if (!storedValue) return [];

      const parsed = JSON.parse(storedValue);
      if (Array.isArray(parsed)) return normalizeItems(parsed);
      if (parsed?.version !== STORAGE_VERSION) return [];

      return normalizeItems(parsed.items);
    } catch (_error) {
      return [];
    }
  }

  function persistItems() {
    try {
      window.localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          version: STORAGE_VERSION,
          items,
        })
      );
    } catch (_error) {
      // The current page still keeps working when storage is unavailable.
    }
  }

  function itemCountLabel(count) {
    return translateCount('item_one', 'item_other', count);
  }

  function announce(message) {
    const regions = document.querySelectorAll('[data-verella-wishlist-live]');
    for (const region of regions) region.textContent = '';

    window.setTimeout(() => {
      for (const region of regions) region.textContent = message;
    }, 30);
  }

  function setActionStatus(message = '', state = '') {
    window.clearTimeout(statusTimer);

    for (const region of document.querySelectorAll('[data-verella-wishlist-action-status]')) {
      region.textContent = message;
      if (state) region.dataset.state = state;
      else delete region.dataset.state;
    }

    if (message) announce(message);

    if (message && state !== 'error') {
      statusTimer = window.setTimeout(() => setActionStatus(), 5000);
    }
  }

  function setEmptyStateStatus(message = '', state = '') {
    for (const emptyState of document.querySelectorAll('[data-verella-wishlist-empty]')) {
      let region = emptyState.querySelector('[data-verella-wishlist-empty-status]');

      if (!region && message) {
        region = document.createElement('p');
        region.className = 'verella-wishlist-drawer__action-status';
        region.dataset.verellaWishlistEmptyStatus = '';
        region.setAttribute('role', 'status');
        region.setAttribute('aria-live', 'polite');
        const shopLink = emptyState.querySelector('.verella-wishlist-drawer__shop-link');
        if (shopLink) shopLink.before(region);
        else emptyState.append(region);
      }

      if (!region) continue;
      region.textContent = message;
      region.hidden = !message;
      if (state) region.dataset.state = state;
      else delete region.dataset.state;
    }
  }

  function showUnavailableSharedWishlist() {
    const message = translate('shared_unavailable');
    openDrawer(document.activeElement);
    setActionStatus(message, 'error');
    if (!drawerItems().length) setEmptyStateStatus(message, 'error');
  }

  function matchingWishlistButton(item) {
    const buttons = [...document.querySelectorAll('[data-verella-wishlist-toggle]')];
    const productId = cleanText(item.id, 80);
    const handle = safeHandle(item.handle);

    return (
      buttons.find((button) => cleanText(button.dataset.wishlistProductId, 80) === productId) ||
      buttons.find((button) => handle && safeHandle(button.dataset.wishlistHandle) === handle) ||
      null
    );
  }

  async function repairShareItem(item) {
    let repairedItem = item;

    if (!repairedItem.handle || !repairedItem.variantId) {
      const matchingButton = matchingWishlistButton(repairedItem);
      const buttonItem = matchingButton ? productFromButton(matchingButton) : null;

      if (buttonItem) {
        repairedItem =
          normalizeItem({
            ...repairedItem,
            handle: repairedItem.handle || buttonItem.handle,
            variantId: repairedItem.variantId || buttonItem.variantId,
            variantTitle: repairedItem.variantTitle || buttonItem.variantTitle,
            url:
              productReferenceFromUrl(repairedItem.url).handle && repairedItem.url
                ? repairedItem.url
                : buttonItem.url,
          }) || repairedItem;
      }
    }

    if (repairedItem.handle && repairedItem.variantId) return repairedItem;
    if (!repairedItem.handle) return repairedItem;

    try {
      const product = await getProduct(repairedItem.handle);
      const variants = Array.isArray(product?.variants) ? product.variants : [];
      const referencedVariantId = safeVariantId(repairedItem.variantId);
      const variant =
        variants.find((candidate) => safeVariantId(candidate.id) === referencedVariantId) ||
        variants.find((candidate) => candidate.available) ||
        variants[0];
      const handle = safeHandle(product?.handle || repairedItem.handle);
      const variantId = safeVariantId(variant?.id);

      if (!handle || !variantId) return repairedItem;

      return (
        normalizeItem({
          ...repairedItem,
          handle,
          variantId,
          variantTitle: repairedItem.variantTitle || variant.title,
          url: `${storefrontRoot()}products/${handle}?variant=${variantId}`,
        }) || repairedItem
      );
    } catch (_error) {
      return repairedItem;
    }
  }

  async function repairShareItems() {
    const currentItems = drawerItems();
    const repairResults = await mapWithConcurrency(
      currentItems,
      PRODUCT_FETCH_CONCURRENCY,
      repairShareItem
    );
    const repairedItems = repairResults.map((item, index) => item || currentItems[index]);

    if (Array.isArray(sharedItems)) {
      sharedItems = normalizeItems(repairedItems);
      renderDrawer();
    } else {
      const referencesChanged = repairedItems.some(
        (item, index) =>
          item.handle !== currentItems[index]?.handle ||
          item.variantId !== currentItems[index]?.variantId ||
          item.url !== currentItems[index]?.url
      );

      if (referencesChanged) setItems(repairedItems);
      else persistItems();
    }

    return repairedItems;
  }

  function shareReferences(currentItems) {
    if (currentItems.length > SHARE_MAX_ITEMS) throw new Error('share-link-too-long');

    return currentItems.map((item) => {
      const handle = safeHandle(item.handle);
      const variantId = variantIdToBase36(item.variantId);
      if (!handle || !variantId) throw new Error('share-unavailable');
      // Only use RFC 3986 unreserved characters. Commas are often treated as
      // the end of a URL by messaging apps, which used to cut shared lists
      // after the first product.
      return `${handle}~${variantId}`;
    });
  }

  async function createShareUrl() {
    const url = new URL(storefrontRoot(), window.location.origin);
    const references = shareReferences(await repairShareItems());
    if (references.length) {
      // Keep the shared wishlist in the regular query string. Messaging and
      // social apps commonly remove URL fragments (`#…`) while forwarding a
      // link, which previously left recipients on the homepage without the
      // shared items. The references use query-safe characters, so assigning
      // the query directly also avoids URLSearchParams expanding every `:`
      // and `,`, keeping larger wishlists comfortably below the length limit.
      // The importer still accepts legacy hash links below.
      const wishlistParameter = `${SHARE_PARAMETER}=${SHARE_VERSION}.${references.join('.')}`;
      const themeId = previewThemeId();

      // A preview cookie only exists in the browser that opened the Theme
      // Editor. Carry the unpublished theme ID in shared preview links so a
      // recipient loads the same wishlist code instead of the live homepage.
      // Published-theme links stay clean and contain only the wishlist.
      url.search = themeId
        ? `?preview_theme_id=${themeId}&${wishlistParameter}`
        : `?${wishlistParameter}`;
    }
    if (url.href.length > SHARED_LINK_MAX_LENGTH) throw new Error('share-link-too-long');
    return url.href;
  }

  function sharedReferencesFromUrl() {
    let value = '';

    try {
      const url = new URL(window.location.href);
      const hash = new URLSearchParams(url.hash.replace(/^#/, ''));
      value = url.searchParams.get(SHARE_PARAMETER) || hash.get(SHARE_PARAMETER) || '';
    } catch (_error) {
      return [];
    }

    if (value.length > SHARED_LINK_MAX_LENGTH) return [];

    const separatorIndex = value.indexOf('.');
    if (separatorIndex <= 0) return [];

    const version = value.slice(0, separatorIndex);
    if (
      version !== SHARE_VERSION &&
      version !== PREVIOUS_SHARE_VERSION &&
      version !== LEGACY_SHARE_VERSION
    ) return [];

    const seen = new Set();
    const references = [];
    const encodedReferences = value.slice(separatorIndex + 1).split(
      version === SHARE_VERSION ? '.' : ','
    );

    for (const token of encodedReferences) {
      const tokenSeparatorIndex = token.lastIndexOf(version === SHARE_VERSION ? '~' : ':');
      if (tokenSeparatorIndex <= 0) continue;

      const handle = safeHandle(token.slice(0, tokenSeparatorIndex));
      const encodedVariantId = token.slice(tokenSeparatorIndex + 1);
      const variantId =
        version === SHARE_VERSION || version === PREVIOUS_SHARE_VERSION
          ? base36ToVariantId(encodedVariantId)
          : safeVariantId(encodedVariantId);
      const key = `${handle}:${variantId}`;
      if (!handle || !variantId || seen.has(key)) continue;

      seen.add(key);
      references.push({ handle, variantId });
      if (references.length >= SHARE_MAX_ITEMS) break;
    }

    return references;
  }

  function hasSharedWishlistParameter() {
    try {
      const url = new URL(window.location.href);
      const hash = new URLSearchParams(url.hash.replace(/^#/, ''));
      return hash.has(SHARE_PARAMETER) || url.searchParams.has(SHARE_PARAMETER);
    } catch (_error) {
      return false;
    }
  }

  async function copyText(value) {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
      return;
    }

    const input = document.createElement('textarea');
    input.value = value;
    input.setAttribute('readonly', '');
    input.style.position = 'fixed';
    input.style.opacity = '0';
    document.body.append(input);
    input.select();
    const copied = document.execCommand('copy');
    input.remove();
    if (!copied) throw new Error('copy-failed');
  }

  async function shareWishlist(button) {
    if (!drawerItems().length) return;

    shareActionInFlight = true;
    button.disabled = true;
    setActionStatus();

    try {
      const url = await createShareUrl();
      const shareData = {
        title: translate('share_title'),
        text: translate('share_text'),
        url,
      };

      if (typeof navigator.share === 'function') {
        try {
          await navigator.share(shareData);
          setActionStatus(translate('share_success'), 'success');
          return;
        } catch (error) {
          if (error?.name === 'AbortError') return;
        }
      }

      await copyText(url);
      setActionStatus(translate('share_copied'), 'success');
    } catch (error) {
      if (error?.message === 'share-link-too-long') {
        setActionStatus(translate('share_too_large'), 'error');
      } else if (error?.message === 'share-unavailable') {
        setActionStatus(translate('share_repair'), 'error');
      } else {
        setActionStatus(translate('share_error'), 'error');
      }
    } finally {
      shareActionInFlight = false;
      button.disabled = drawerItems().length === 0;
    }
  }

  function dispatchChange() {
    document.dispatchEvent(
      new CustomEvent('verella:wishlist:change', {
        detail: {
          count: items.length,
          items: items.map((item) => ({ ...item })),
        },
      })
    );
  }

  function setItems(nextItems, message = '') {
    items = normalizeItems(nextItems);
    persistItems();
    render();
    dispatchChange();
    if (message) announce(message);
  }

  function productEndpoint(handle) {
    return `${storefrontRoot()}products/${encodeURIComponent(handle)}.js`;
  }

  async function getProduct(handle) {
    const safeProductHandle = safeHandle(handle);
    if (!safeProductHandle) return null;
    if (productRequests.has(safeProductHandle)) return productRequests.get(safeProductHandle);

    const request = fetch(productEndpoint(safeProductHandle), {
      headers: { Accept: 'application/json' },
    })
      .then((response) => {
        if (!response.ok) throw new Error('product-unavailable');
        return response.json();
      })
      .catch((error) => {
        productRequests.delete(safeProductHandle);
        throw error;
      });

    productRequests.set(safeProductHandle, request);
    return request;
  }

  function itemFromProductReference(reference, product) {
    if (!product || !Array.isArray(product.variants)) return null;

    const variant = product.variants.find(
      (candidate) => String(candidate.id) === String(reference.variantId)
    );
    if (!variant) return null;

    const handle = safeHandle(product.handle || reference.handle);
    const variantId = safeVariantId(variant.id);
    if (!handle || !variantId) return null;

    const variantImage = variant.featured_image?.src || variant.featured_image;
    const productImage = product.featured_image?.src || product.featured_image || product.images?.[0];

    return normalizeItem({
      id: product.id,
      handle,
      variantId,
      variantTitle: variant.title,
      url: `${storefrontRoot()}products/${handle}?variant=${variantId}`,
      title: product.title,
      image: variantImage || productImage || '',
      price: formatPrice(variant.price),
      addedAt: Date.now(),
    });
  }

  async function importSharedWishlist() {
    const hasSharedLink = hasSharedWishlistParameter();
    const references = sharedReferencesFromUrl();
    if (!references.length) {
      if (hasSharedLink) showUnavailableSharedWishlist();
      return;
    }

    const imported = (
      await mapWithConcurrency(references, PRODUCT_FETCH_CONCURRENCY, async (reference) => {
        const product = await getProduct(reference.handle);
        return itemFromProductReference(reference, product);
      })
    ).filter(Boolean);

    if (!imported.length) {
      showUnavailableSharedWishlist();
      return;
    }

    sharedItems = normalizeItems(imported);
    renderDrawer();
    const importedLabel = translateCount(
      'favorites_loaded_one',
      'favorites_loaded_other',
      imported.length
    );
    setActionStatus(importedLabel, 'success');
    openDrawer(document.activeElement);
  }

  function adoptSharedWishlist() {
    if (!Array.isArray(sharedItems) || !sharedItems.length) return;

    const existingProductIds = new Set(items.map((item) => item.id));
    const newSharedItems = sharedItems.filter((item) => !existingProductIds.has(item.id));
    const duplicateCount = sharedItems.length - newSharedItems.length;
    const availableSlots = Math.max(0, MAX_ITEMS - items.length);
    const importedItems = newSharedItems
      .slice(0, availableSlots)
      .map((item) => ({ ...item, addedAt: Date.now() }));
    const skippedCount = newSharedItems.length - importedItems.length;
    const addedCount = importedItems.length;
    sharedItems = null;
    setItems([...importedItems, ...items]);

    const statusParts = [
      addedCount
        ? translateCount('favorites_saved_one', 'favorites_saved_other', addedCount)
        : translate('none_saved'),
    ];
    if (duplicateCount) {
      statusParts.push(
        translateCount('duplicates_one', 'duplicates_other', duplicateCount)
      );
    }
    if (skippedCount) {
      statusParts.push(
        translateCount('skipped_one', 'skipped_other', skippedCount)
      );
    }
    setActionStatus(statusParts.join(' '), skippedCount ? 'notice' : 'success');
  }

  function hasProduct(productId) {
    const id = cleanText(productId, 80);
    return Boolean(id && items.some((item) => item.id === id));
  }

  function productFromButton(button) {
    return normalizeItem({
      id: button.dataset.wishlistProductId,
      handle: button.dataset.wishlistHandle,
      variantId: button.dataset.wishlistVariantId,
      variantTitle: button.dataset.wishlistVariantTitle,
      url: button.dataset.wishlistUrl,
      title: button.dataset.wishlistTitle,
      image: button.dataset.wishlistImage,
      price: button.dataset.wishlistPrice,
      addedAt: Date.now(),
    });
  }

  function syncButtonFromCardState(button) {
    if (!(button instanceof HTMLElement)) return;

    const productCard = button.closest('product-card');
    const collectionTabShell = button.closest('[class*="ai-product-card-shell-"]');
    const card = productCard || collectionTabShell;
    if (!(card instanceof HTMLElement)) return;

    const cardLink =
      productCard?.refs?.productCardLink ||
      card.querySelector('.product-card__link[href], a[data-product-url][href]');
    const selectedVariantUrl =
      cardLink?.dataset.selectedVariantUrl || cardLink?.getAttribute('href') || '';
    const linkReference = productReferenceFromUrl(selectedVariantUrl);
    const escapedVariantId = window.CSS?.escape
      ? window.CSS.escape(linkReference.variantId)
      : linkReference.variantId;
    const linkedSwatch = escapedVariantId
      ? card.querySelector(
          `[data-variant-id="${escapedVariantId}"][data-variant-url], .verella-swatch-item[data-variant-id="${escapedVariantId}"]`
        )
      : null;
    const activeSwatch = card.querySelector(
      '[data-variant-id][data-variant-url][aria-current="true"], [data-variant-id][data-variant-url].active, .verella-swatch-item.active[data-variant-id]'
    );
    const swatch = linkedSwatch || activeSwatch;

    if (swatch instanceof HTMLElement) {
      const variantId = safeVariantId(swatch.dataset.variantId);
      const variantUrl = cleanText(
        swatch.getAttribute('href') || swatch.dataset.variantUrl,
        1000
      );

      if (variantId) button.dataset.wishlistVariantId = variantId;
      const variantTitle = swatch.dataset.variantTitle || swatch.getAttribute('alt');
      if (variantTitle) {
        button.dataset.wishlistVariantTitle = variantTitle;
      }
      if (variantUrl) button.dataset.wishlistUrl = variantUrl;
      if (swatch.dataset.image) button.dataset.wishlistImage = swatch.dataset.image;
      if (swatch.dataset.variantPrice) {
        button.dataset.wishlistPrice = swatch.dataset.variantPrice;
      }
      return;
    }

    // Products without the custom colour swatches still expose their current
    // variant through the card link after a native variant-picker update.
    if (linkReference.variantId) {
      button.dataset.wishlistVariantId = linkReference.variantId;
      button.dataset.wishlistUrl = safeProductUrl(selectedVariantUrl);
    }
  }

  function syncSavedProductFromButton(button) {
    if (!(button instanceof HTMLElement)) return;

    const nextItem = productFromButton(button);
    if (!nextItem) return;

    const currentItem = items.find((item) => item.id === nextItem.id);
    if (!currentItem) {
      renderButtons();
      return;
    }

    nextItem.addedAt = currentItem.addedAt;
    setItems(items.map((item) => (item.id === nextItem.id ? nextItem : item)));
  }

  function handleCardVariantThumbnailClick(event) {
    if (!(event.target instanceof Element)) return;

    const swatch = event.target.closest('[data-variant-id][data-variant-url]');
    if (!(swatch instanceof HTMLElement)) return;

    const card = swatch.closest(
      'product-card, [class*="ai-product-card-shell-"]'
    );
    const button = card?.querySelector('[data-verella-wishlist-toggle]');
    if (!(button instanceof HTMLElement)) return;

    // Collection Tabs update their image and link in their own click handler.
    // Run on the next frame so we read that final visible variant, then also
    // repair an item that was already saved before the colour was changed.
    window.requestAnimationFrame(() => {
      syncButtonFromCardState(button);
      syncSavedProductFromButton(button);
    });
  }

  function syncWishlistButton(button, sourceButton) {
    if (!(button instanceof HTMLElement) || !(sourceButton instanceof HTMLElement)) return;

    const attributes = [
      'data-wishlist-variant-id',
      'data-wishlist-variant-title',
      'data-wishlist-url',
      'data-wishlist-image',
      'data-wishlist-price',
    ];

    for (const attribute of attributes) {
      const value = sourceButton.getAttribute(attribute);
      if (value === null) button.removeAttribute(attribute);
      else button.setAttribute(attribute, value);
    }
  }

  function handleVariantUpdate(event) {
    if (!(event.target instanceof Element)) return;

    const productRoot = event.target.closest(
      'product-card, quick-add-component, featured-product-information, [data-testid="product-information"], .product-information, .shopify-section, section'
    );
    if (!productRoot) return;

    const eventProductId = cleanText(
      event.detail?.data?.newProduct?.id || event.detail?.data?.productId || event.detail?.resource?.product_id,
      80
    );
    const liveButtons = [...productRoot.querySelectorAll('[data-verella-wishlist-toggle]')];
    const liveButton =
      liveButtons.find((button) => button.dataset.wishlistProductId === eventProductId) ||
      (liveButtons.length === 1 ? liveButtons[0] : null);
    const productId = cleanText(liveButton?.dataset.wishlistProductId || eventProductId, 80);
    const sourceButtons = [
      ...(event.detail?.data?.html?.querySelectorAll?.('[data-verella-wishlist-toggle]') || []),
    ];
    const sourceButton =
      sourceButtons.find((button) => button.dataset.wishlistProductId === productId) ||
      (sourceButtons.length === 1 ? sourceButtons[0] : null);
    if (!sourceButton || !liveButton) return;

    syncWishlistButton(liveButton, sourceButton);
    syncSavedProductFromButton(liveButton);
  }

  function toggleProduct(button) {
    syncButtonFromCardState(button);
    const item = productFromButton(button);
    if (!item) return;

    if (hasProduct(item.id)) {
      setItems(
        items.filter((candidate) => candidate.id !== item.id),
        translate('removed', { title: item.title })
      );
      return;
    }

    setItems([item, ...items], translate('added', { title: item.title }));
  }

  function removeProduct(productId) {
    const id = cleanText(productId, 80);
    const currentItems = drawerItems();
    const item = currentItems.find((candidate) => candidate.id === id);
    if (!item) return;

    if (Array.isArray(sharedItems)) {
      sharedItems = sharedItems.filter((candidate) => candidate.id !== id);
      renderDrawer();
      announce(translate('removed_shared', { title: item.title }));
      return;
    }

    setItems(
      items.filter((candidate) => candidate.id !== id),
      translate('removed', { title: item.title })
    );
  }

  async function resolveCartItem(item) {
    const handle = safeHandle(item.handle);
    if (!handle) return null;

    const product = await getProduct(handle);
    const variants = Array.isArray(product?.variants) ? product.variants : [];
    const storedVariantId = safeVariantId(item.variantId);
    const urlVariantId = productReferenceFromUrl(item.url).variantId;
    const normalizedTitle = cleanText(item.variantTitle, 300).toLocaleLowerCase();
    const storedVariant = variants.find(
      (candidate) => safeVariantId(candidate.id) === storedVariantId
    );
    const urlVariant = variants.find(
      (candidate) => safeVariantId(candidate.id) === urlVariantId
    );
    const titleVariant = normalizedTitle && normalizedTitle !== 'default title'
      ? variants.find(
          (candidate) => cleanText(candidate.title, 300).toLocaleLowerCase() === normalizedTitle
        )
      : null;

    // Old wishlist entries can contain a stale first-variant ID while their
    // URL and visible title already point to the colour the customer chose.
    // Two agreeing signals win; otherwise the variant-specific URL is the
    // strongest source, followed by the visible title and the stored ID.
    const urlMatchesTitle =
      urlVariant && titleVariant && String(urlVariant.id) === String(titleVariant.id);
    const storedMatchesTitle =
      storedVariant && titleVariant && String(storedVariant.id) === String(titleVariant.id);
    const variant = urlMatchesTitle
      ? urlVariant
      : storedMatchesTitle
        ? storedVariant
        : urlVariant || titleVariant || storedVariant;
    const minimumQuantity = Number(variant?.quantity_rule?.min || 1);
    if (
      !variant ||
      !variant.available ||
      variant.requires_selling_plan ||
      product.requires_selling_plan ||
      minimumQuantity > 1
    ) return null;

    return {
      id: safeVariantId(variant.id),
      quantity: 1,
    };
  }

  function sectionIdsForCart() {
    return Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
      .map((component) => component.dataset.sectionId)
      .filter((id, index, values) => Boolean(id) && values.indexOf(id) === index)
      .slice(0, 5);
  }

  async function fetchCart(dialog) {
    const response = await fetch(dialog.dataset.cartUrl || `${storefrontRoot()}cart.js`, {
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error('cart-unavailable');
    return response.json();
  }

  function variantQuantities(cart) {
    const quantities = new Map();
    for (const item of cart?.items || []) {
      quantities.set(String(item.variant_id), Number(item.quantity) || 0);
    }
    return quantities;
  }

  function addedTargetCount(beforeCart, afterCart, cartItems) {
    const before = variantQuantities(beforeCart);
    const after = variantQuantities(afterCart);
    return cartItems.reduce((count, item) => {
      const variantId = String(item.id);
      return count + ((after.get(variantId) || 0) > (before.get(variantId) || 0) ? 1 : 0);
    }, 0);
  }

  function safeCartItemCount(cart) {
    const value = cart?.item_count;
    if (typeof value !== 'number' && typeof value !== 'string') return null;

    const count = Number(value);
    return Number.isSafeInteger(count) && count >= 0 ? count : null;
  }

  function dispatchCartUpdate(cart, sections = {}, cartItems = []) {
    const itemCount = safeCartItemCount(cart);
    const data = {
      source: 'verella-wishlist',
      variantId: cartItems[0]?.id ? String(cartItems[0].id) : '',
      quantity: cartItems.length,
      sections,
    };
    if (itemCount !== null) data.itemCount = itemCount;

    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: cart,
          sourceId: 'verella-wishlist-dialog',
          data,
        },
      })
    );
  }

  function cartSnapshotAfterAdd(cart, beforeCart, addedCount) {
    if (safeCartItemCount(cart) !== null) return cart;

    const previousItemCount = safeCartItemCount(beforeCart);
    if (previousItemCount === null || !Number.isSafeInteger(addedCount) || addedCount < 0) return cart;

    return {
      ...beforeCart,
      item_count: Math.max(0, previousItemCount + addedCount),
    };
  }

  async function addAllToCart(button) {
    const currentItems = drawerItems();
    if (!currentItems.length || button.disabled || cartActionInFlight) return;

    const dialog = button.closest('[data-verella-wishlist-dialog]');
    if (!(dialog instanceof HTMLDialogElement)) return;

    const operationToken = ++cartActionToken;
    const capturedItems = currentItems.map((item) => ({ ...item }));
    const label = button.querySelector('[data-verella-wishlist-add-all-label]');
    const defaultLabel = translate('add_all');

    cartActionInFlight = true;
    button.disabled = true;
    dialog.setAttribute('aria-busy', 'true');
    if (label) label.textContent = translate('checking');
    setActionStatus();

    try {
      const resolvedItems = await mapWithConcurrency(
        capturedItems,
        PRODUCT_FETCH_CONCURRENCY,
        resolveCartItem
      );
      if (operationToken !== cartActionToken) return;

      const cartItemsByVariant = new Map();
      for (const cartItem of resolvedItems.filter(Boolean)) {
        cartItemsByVariant.set(String(cartItem.id), cartItem);
      }
      const cartItems = [...cartItemsByVariant.values()];
      const unavailableCount = capturedItems.length - cartItems.length;

      if (!cartItems.length) {
        setActionStatus(translate('variants_unavailable'), 'error');
        return;
      }

      if (label) label.textContent = translate('adding');

      let beforeCart = null;
      try {
        beforeCart = await fetchCart(dialog);
      } catch (_error) {
        // The add request can still proceed when the cart snapshot is unavailable.
      }

      const sectionIds = sectionIdsForCart();
      const payload = {
        items: cartItems,
        sections_url: window.location.pathname,
      };
      if (sectionIds.length) payload.sections = sectionIds;

      if (!cartActionInFlight || operationToken !== cartActionToken) return;

      const response = await fetch(dialog.dataset.cartAddUrl || `${storefrontRoot()}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(payload),
      });
      const result = await response.json().catch(() => ({}));

      let cart = null;
      try {
        cart = await fetchCart(dialog);
      } catch (_error) {
        // Section HTML from cart/add can still update the cart UI.
      }

      if (operationToken !== cartActionToken) return;

      let addedCount = response.ok && !result.status ? cartItems.length : 0;
      if (!addedCount && beforeCart && cart) {
        addedCount = addedTargetCount(beforeCart, cart, cartItems);
      }

      if (!addedCount) {
        throw new Error(result.description || result.message || translate('add_failed'));
      }

      const skippedCount = unavailableCount + Math.max(0, cartItems.length - addedCount);
      const successMessage = skippedCount
        ? translate('added_partial', { added: addedCount, skipped: skippedCount })
        : translateCount('added_one', 'added_other', addedCount);
      setActionStatus(successMessage, skippedCount ? 'notice' : 'success');

      const autoOpeningCart = document.querySelector('cart-drawer-component[auto-open]');
      if (autoOpeningCart) finishClose(dialog, false);
      dispatchCartUpdate(cartSnapshotAfterAdd(cart, beforeCart, addedCount), result.sections || {}, cartItems);
    } catch (error) {
      if (operationToken !== cartActionToken) return;
      setActionStatus(error.message || translate('generic_error'), 'error');
    } finally {
      if (operationToken === cartActionToken) {
        cartActionInFlight = false;
        dialog.removeAttribute('aria-busy');
        if (label) label.textContent = defaultLabel;
        button.disabled = drawerItems().length === 0;
      }
    }
  }

  function renderButtons() {
    const activeProductIds = new Set(items.map((item) => item.id));

    for (const button of document.querySelectorAll('[data-verella-wishlist-toggle]')) {
      const productId = cleanText(button.dataset.wishlistProductId, 80);
      const active = activeProductIds.has(productId);
      const title = cleanText(button.dataset.wishlistTitle, 300);
      const addLabel = cleanText(button.dataset.wishlistLabelAdd, 160) || translate('add_label');
      const removeLabel =
        cleanText(button.dataset.wishlistLabelRemove, 160) || translate('remove_label');
      const label = active ? removeLabel : addLabel;

      button.dataset.active = String(active);
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', String(active));
      button.setAttribute('aria-label', title ? `${label}: ${title}` : label);

      const labelElement = button.querySelector('[data-verella-wishlist-label]');
      if (labelElement) labelElement.textContent = label;
    }
  }

  function renderTriggers() {
    const dialogOpen = Boolean(document.querySelector('[data-verella-wishlist-dialog][open]'));
    const countLabel = itemCountLabel(items.length);

    for (const trigger of document.querySelectorAll('[data-verella-wishlist-open]')) {
      trigger.setAttribute('aria-expanded', String(dialogOpen));
      trigger.setAttribute(
        'aria-label',
        items.length > 0 ? translate('open_count', { count: countLabel }) : translate('open')
      );
    }

    for (const counter of document.querySelectorAll('[data-verella-wishlist-count]')) {
      counter.textContent = items.length > 99 ? '99+' : String(items.length);
      counter.hidden = items.length === 0;
    }
  }

  function createSvg(viewBox, pathData) {
    const namespace = 'http://www.w3.org/2000/svg';
    const svg = document.createElementNS(namespace, 'svg');
    const path = document.createElementNS(namespace, 'path');

    svg.setAttribute('viewBox', viewBox);
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');
    path.setAttribute('d', pathData);
    svg.append(path);

    return svg;
  }

  function createImagePlaceholder() {
    const placeholder = document.createElement('span');
    placeholder.className = 'verella-wishlist-drawer__image-placeholder';
    placeholder.setAttribute('aria-hidden', 'true');
    placeholder.append(
      createSvg(
        '0 0 20 20',
        'M10 5.24 8.51 3.77a4.42 4.42 0 0 0-6.21 0 4.33 4.33 0 0 0 0 6.13L10 17.5l7.7-7.6a4.33 4.33 0 0 0 0-6.13 4.42 4.42 0 0 0-6.21 0L10 5.24Z'
      )
    );
    return placeholder;
  }

  function createDrawerItem(item) {
    const listItem = document.createElement('li');
    listItem.className = 'verella-wishlist-drawer__item';
    listItem.dataset.wishlistItemId = item.id;

    const mediaLink = document.createElement('a');
    mediaLink.className = 'verella-wishlist-drawer__media';
    mediaLink.href = item.url;
    mediaLink.setAttribute('aria-label', item.title);

    if (item.image) {
      const image = document.createElement('img');
      image.className = 'verella-wishlist-drawer__image';
      image.src = item.image;
      image.alt = item.title;
      image.loading = 'lazy';
      image.decoding = 'async';
      image.addEventListener(
        'error',
        () => {
          image.replaceWith(createImagePlaceholder());
        },
        { once: true }
      );
      mediaLink.append(image);
    } else {
      mediaLink.append(createImagePlaceholder());
    }

    const content = document.createElement('div');
    content.className = 'verella-wishlist-drawer__item-content';

    const title = document.createElement('a');
    title.className = 'verella-wishlist-drawer__item-title';
    title.href = item.url;
    title.textContent = item.title;
    content.append(title);

    if (item.variantTitle && item.variantTitle !== 'Default Title') {
      const variantTitle = document.createElement('p');
      variantTitle.className = 'verella-wishlist-drawer__item-variant';
      variantTitle.textContent = item.variantTitle;
      content.append(variantTitle);
    }

    if (item.price) {
      const price = document.createElement('p');
      price.className = 'verella-wishlist-drawer__item-price';
      price.textContent = item.price;
      content.append(price);
    }

    const removeButton = document.createElement('button');
    removeButton.type = 'button';
    removeButton.className = 'verella-wishlist-drawer__remove';
    removeButton.dataset.verellaWishlistRemove = item.id;
    removeButton.setAttribute('aria-label', translate('remove_item', { title: item.title }));
    removeButton.append(
      createSvg(
        '0 0 20 20',
        'M10 17.35 3.2 10.7A4.45 4.45 0 0 1 9.5 4.4l.5.5.5-.5a4.45 4.45 0 0 1 6.3 6.3L10 17.35Z'
      )
    );

    const removeLabel = document.createElement('span');
    removeLabel.className = 'visually-hidden';
    removeLabel.textContent = translate('remove');
    removeButton.append(removeLabel);

    listItem.append(mediaLink, removeButton, content);
    return listItem;
  }

  function renderDrawer() {
    const currentItems = drawerItems();
    const sharedMode = Array.isArray(sharedItems);

    for (const count of document.querySelectorAll('[data-verella-wishlist-drawer-count]')) {
      count.textContent = sharedMode
        ? translate('shared_list', { count: itemCountLabel(currentItems.length) })
        : itemCountLabel(currentItems.length);
    }

    for (const emptyState of document.querySelectorAll('[data-verella-wishlist-empty]')) {
      emptyState.hidden = currentItems.length > 0;
    }

    for (const footer of document.querySelectorAll('[data-verella-wishlist-footer]')) {
      footer.hidden = currentItems.length === 0;
    }

    for (const list of document.querySelectorAll('[data-verella-wishlist-list]')) {
      list.hidden = currentItems.length === 0;
      const fragment = document.createDocumentFragment();
      for (const item of currentItems) fragment.append(createDrawerItem(item));
      list.replaceChildren(fragment);
    }

    for (const button of document.querySelectorAll('[data-verella-wishlist-share]')) {
      button.disabled = currentItems.length === 0 || shareActionInFlight;
    }

    for (const button of document.querySelectorAll('[data-verella-wishlist-add-all]')) {
      button.disabled = currentItems.length === 0 || cartActionInFlight;
    }

    for (const button of document.querySelectorAll('[data-verella-wishlist-adopt]')) {
      button.hidden = !sharedMode || currentItems.length === 0;
    }

    if (currentItems.length) setEmptyStateStatus();
    else setActionStatus();
  }

  function render() {
    renderButtons();
    renderTriggers();
    renderDrawer();
  }

  function scheduleRender() {
    if (renderFrame) return;
    renderFrame = window.requestAnimationFrame(() => {
      renderFrame = undefined;
      bindDrawers();
      render();
    });
  }

  function setTriggerExpansion(expanded) {
    for (const trigger of document.querySelectorAll('[data-verella-wishlist-open]')) {
      trigger.setAttribute('aria-expanded', String(expanded));
    }
  }

  function openDrawer(trigger) {
    const dialog = document.querySelector('[data-verella-wishlist-dialog]');
    if (!(dialog instanceof HTMLDialogElement) || dialog.open) return;

    lastFocusedElement = trigger instanceof HTMLElement ? trigger : document.activeElement;
    dialog.classList.remove('is-closing');
    renderDrawer();
    dialog.showModal();
    html.classList.add('verella-wishlist-is-open');
    setTriggerExpansion(true);

    window.requestAnimationFrame(() => {
      dialog.querySelector('[data-verella-wishlist-close]')?.focus({ preventScroll: true });
    });
  }

  function finishClose(dialog, restoreFocus = true) {
    window.clearTimeout(closeTimer);
    closeTimer = undefined;

    if (dialog.open) dialog.close();
    dialog.classList.remove('is-closing');
    html.classList.remove('verella-wishlist-is-open');
    setTriggerExpansion(false);

    if (restoreFocus && lastFocusedElement instanceof HTMLElement && lastFocusedElement.isConnected) {
      lastFocusedElement.focus({ preventScroll: true });
    }
    lastFocusedElement = undefined;
  }

  function closeDrawer() {
    const dialog = document.querySelector('[data-verella-wishlist-dialog]');
    if (!(dialog instanceof HTMLDialogElement) || !dialog.open || dialog.classList.contains('is-closing')) return;

    dialog.classList.add('is-closing');
    setTriggerExpansion(false);

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    closeTimer = window.setTimeout(() => finishClose(dialog), reducedMotion ? 0 : DRAWER_CLOSE_DURATION);
  }

  function bindDrawers() {
    for (const dialog of document.querySelectorAll('[data-verella-wishlist-dialog]')) {
      if (!(dialog instanceof HTMLDialogElement) || dialog.dataset.wishlistBound === 'true') continue;
      dialog.dataset.wishlistBound = 'true';

      dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        closeDrawer();
      });

      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) closeDrawer();
      });

      dialog.addEventListener('close', () => {
        dialog.classList.remove('is-closing');
        html.classList.remove('verella-wishlist-is-open');
        setTriggerExpansion(false);
      });
    }
  }

  function handleClick(event) {
    if (!(event.target instanceof Element)) return;

    const toggle = event.target.closest('[data-verella-wishlist-toggle]');
    if (toggle instanceof HTMLButtonElement) {
      event.preventDefault();
      event.stopPropagation();
      toggleProduct(toggle);
      return;
    }

    const opener = event.target.closest('[data-verella-wishlist-open]');
    if (opener instanceof HTMLElement) {
      event.preventDefault();
      event.stopPropagation();
      openDrawer(opener);
      return;
    }

    const closer = event.target.closest('[data-verella-wishlist-close]');
    if (closer instanceof HTMLElement) {
      event.preventDefault();
      event.stopPropagation();
      closeDrawer();
      return;
    }

    const shareButton = event.target.closest('[data-verella-wishlist-share]');
    if (shareButton instanceof HTMLButtonElement) {
      event.preventDefault();
      event.stopPropagation();
      shareWishlist(shareButton);
      return;
    }

    const addAllButton = event.target.closest('[data-verella-wishlist-add-all]');
    if (addAllButton instanceof HTMLButtonElement) {
      event.preventDefault();
      event.stopPropagation();
      addAllToCart(addAllButton);
      return;
    }

    const adoptButton = event.target.closest('[data-verella-wishlist-adopt]');
    if (adoptButton instanceof HTMLButtonElement) {
      event.preventDefault();
      event.stopPropagation();
      adoptSharedWishlist();
      return;
    }

    const removeButton = event.target.closest('[data-verella-wishlist-remove]');
    if (removeButton instanceof HTMLButtonElement) {
      event.preventDefault();
      event.stopPropagation();
      const list = removeButton.closest('[data-verella-wishlist-list]');
      const currentButtons = list
        ? [...list.querySelectorAll('[data-verella-wishlist-remove]')]
        : [];
      const currentIndex = currentButtons.indexOf(removeButton);
      removeProduct(removeButton.dataset.verellaWishlistRemove);

      window.requestAnimationFrame(() => {
        const remainingButtons = list
          ? [...list.querySelectorAll('[data-verella-wishlist-remove]')]
          : [];
        const nextButton = remainingButtons[Math.min(Math.max(currentIndex, 0), remainingButtons.length - 1)];
        const fallback = document.querySelector(
          '[data-verella-wishlist-dialog][open] [data-verella-wishlist-close], [data-verella-wishlist-dialog][open] .verella-wishlist-drawer__shop-link'
        );
        (nextButton || fallback)?.focus({ preventScroll: true });
      });
    }
  }

  function watchDynamicContent() {
    if (!document.body) return;

    const observer = new MutationObserver((mutations) => {
      const relevantMutation = mutations.some((mutation) =>
        [...mutation.addedNodes].some(
          (node) =>
            node instanceof Element &&
            (node.matches(
              '[data-verella-wishlist-toggle], [data-verella-wishlist-dialog], [data-verella-wishlist-open], [data-verella-wishlist-count]'
            ) ||
              node.querySelector(
                '[data-verella-wishlist-toggle], [data-verella-wishlist-dialog], [data-verella-wishlist-open], [data-verella-wishlist-count]'
              ))
        )
      );

      if (relevantMutation) scheduleRender();
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  function init() {
    items = readItems();
    bindDrawers();
    render();
    watchDynamicContent();

    document.addEventListener('click', handleClick, true);
    document.addEventListener('click', handleCardVariantThumbnailClick);
    document.addEventListener('variant:update', handleVariantUpdate);
    document.addEventListener('shopify:section:load', scheduleRender);

    window.addEventListener('storage', (event) => {
      if (event.key !== STORAGE_KEY) return;
      items = readItems();
      render();
      dispatchChange();
    });

    importSharedWishlist();
  }

  window.VerellaWishlist = Object.freeze({
    storageKey: STORAGE_KEY,
    getItems: () => items.map((item) => ({ ...item })),
    has: hasProduct,
    open: () => openDrawer(document.activeElement),
    close: closeDrawer,
    remove: removeProduct,
    syncButton: syncSavedProductFromButton,
    shareUrl: createShareUrl,
    addAllToCart: () => {
      const button = document.querySelector('[data-verella-wishlist-add-all]');
      if (button instanceof HTMLButtonElement) return addAllToCart(button);
    },
  });

  init();
}
